// [Codex 2026-07-05] CAO history rail animates open/close instead of snapping into the shell.
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { useBackend } from "../../lib/backend";
import {
  groupConversationsByDate,
  type ConversationSummary,
} from "./session-history-utils";
import { SessionsRail } from "./SessionsRail";
import { publishDeletedConversation } from "./conversation-events";

interface CaoConversationHistoryRailProps {
  open: boolean;
  onClose: () => void;
}

export function CaoConversationHistoryRail({
  open,
  onClose,
}: CaoConversationHistoryRailProps) {
  const backend = useBackend();
  const [sessions, setSessions] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    backend.ai
      .listConversations({ surface: "chat" })
      .then((data) => setSessions(data ?? []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [backend]);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => searchRef.current?.focus());
  }, [open]);

  const filtered = search.trim()
    ? sessions.filter((session) =>
        session.title.toLowerCase().includes(search.toLowerCase()),
      )
    : sessions;
  const grouped = groupConversationsByDate(filtered);
  const flatFiltered = Object.values(grouped).flat();

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  useEffect(() => {
    const item = listRef.current?.querySelector(
      `[data-index="${selectedIndex}"]`,
    );
    item?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const handleSelectSession = useCallback((id: string) => {
    window.dispatchEvent(
      new CustomEvent("fintheon:chat-load-session", { detail: { id } }),
    );
  }, []);

  const handleNewSession = useCallback(() => {
    window.dispatchEvent(new Event("fintheon:chat-new"));
  }, []);

  const handleDelete = useCallback(
    async (id: string, event: MouseEvent) => {
      event.stopPropagation();
      const confirmed = window.confirm(
        "Delete this conversation permanently? This cannot be undone.",
      );
      if (!confirmed) return;
      try {
        await backend.ai.deleteConversation(id);
        setSessions((prev) => prev.filter((session) => session.id !== id));
        publishDeletedConversation(id);
      } catch {
        window.alert(
          "This conversation could not be deleted. Please try again.",
        );
      }
    },
    [backend],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((index) =>
          Math.min(index + 1, flatFiltered.length - 1),
        );
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((index) => Math.max(index - 1, 0));
      }
      if (event.key === "Enter" && flatFiltered[selectedIndex]) {
        handleSelectSession(flatFiltered[selectedIndex].id);
      }
    },
    [flatFiltered, handleSelectSession, onClose, selectedIndex],
  );

  return (
    <aside
      className={`fintheon-side-surface fintheon-shell-side-panel fintheon-drawer-left flex h-full shrink-0 flex-col overflow-hidden transition-[width,transform,opacity] duration-[320ms] ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none ${
        open
          ? "w-[min(292px,calc(100vw-72px))] opacity-100 md:w-[min(320px,32vw)]"
          : "w-0 opacity-0"
      }`}
      style={{
        transform: open ? "translate3d(0,0,0)" : "translate3d(-12px,0,0)",
      }}
      data-cao-history-rail="true"
      data-open={open ? "true" : "false"}
      aria-hidden={!open}
      onKeyDown={handleKeyDown}
    >
      <SessionsRail
        search={search}
        onSearchChange={setSearch}
        onClose={onClose}
        onNewSession={handleNewSession}
        loading={loading}
        grouped={grouped}
        flatSessions={flatFiltered}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        onSelectSession={handleSelectSession}
        onDeleteSession={handleDelete}
        searchRef={searchRef}
        listRef={listRef}
        variant="shell"
      />
    </aside>
  );
}
